/**
 * GlobalVoiceOverlay Component
 * Phase D4.5.1: Global Voice Overlay Refactor
 * Phase D4.5.2: Uses ZaxiaVoiceEngine for centralized voice processing
 * Phase D4.5.5: Demo Mode support with failure tracking
 * Phase P2.4-1: Voice Overlay UX Redesign (Non-Blocking Mode)
 *
 * A system-level overlay for voice interaction that:
 * - Shows a floating voice bar at bottom-center (non-blocking)
 * - Never navigates away from current view
 * - Auto-hides when voice interaction ends
 * - Reflects voice state: idle → listening → processing → speaking → idle
 * - Keeps concierge interface visible and interactive
 *
 * This component DELEGATES all voice parsing to ZaxiaVoiceEngine.
 * It only HANDLES the resulting VoiceActions.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  askConcierge,
  ConciergeIntent,
  ApiGuest,
  CreateBookingRequest,
  createGuestRequest,
  GuestRequestCategory,
} from '@/api';
import { zaxiaVoice, createSpeechRecognition, isSpeechRecognitionAvailable, type SpeechRecognitionEvent } from '@/services';
import { useGuestContext } from '@/context';
import { useData } from '@/providers/DataProvider';
import { useDemoMode, MAX_CONSECUTIVE_FAILURES, SILENCE_TIMEOUT_MS } from '@/providers/DemoModeProvider';
import { processVoiceInput, createVoiceContext, type VoiceAction } from '@/voice';
import { FloatingVoiceBar } from './FloatingVoiceBar';
import type { Booking } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export type VoiceState =
  | 'idle'        // Overlay hidden, waiting for activation
  | 'listening'   // Actively recording speech
  | 'processing'  // Processing voice / thinking
  | 'speaking';   // Zaxia is speaking response

// Day name mapping for Green Stay (kept for handleVoiceAction)
const DAY_NAME_MAP: Record<string, number> = {
  sunday: 0, sun: 0,
  monday: 1, mon: 1,
  tuesday: 2, tue: 2, tues: 2,
  wednesday: 3, wed: 3,
  thursday: 4, thu: 4, thurs: 4,
  friday: 5, fri: 5,
  saturday: 6, sat: 6,
};

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

function getGuestDisplayName(guest?: ApiGuest | null): string {
  if (!guest) return 'Guest';
  if (guest.lastName) {
    return guest.firstName ? `${guest.firstName}` : guest.lastName;
  }
  return guest.firstName || 'Guest';
}

function parseDayReference(dayRef: string): string | null {
  const today = new Date();
  const ref = dayRef.toLowerCase().trim();

  if (ref === 'today') {
    return today.toISOString().split('T')[0]!;
  }

  if (ref === 'tomorrow') {
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0]!;
  }

  if (ref in DAY_NAME_MAP) {
    const targetDay = DAY_NAME_MAP[ref]!;
    const currentDay = today.getDay();
    let daysAhead = targetDay - currentDay;
    if (daysAhead < 0) daysAhead += 7; // only wrap if strictly in the past, not same-day
    const targetDate = new Date(today);
    targetDate.setDate(today.getDate() + daysAhead);
    return targetDate.toISOString().split('T')[0]!;
  }

  const dateMatch = ref.match(/(\d{1,2})(st|nd|rd|th)?/);
  if (dateMatch) {
    const dayNum = parseInt(dateMatch[1]!, 10);
    if (dayNum >= 1 && dayNum <= 31) {
      const todayStr = today.toISOString().split('T')[0]!; // YYYY-MM-DD, no time
      const targetDate = new Date(today.getFullYear(), today.getMonth(), dayNum);
      const targetStr = targetDate.toISOString().split('T')[0]!;
      if (targetStr < todayStr) {
        targetDate.setMonth(targetDate.getMonth() + 1);
      }
      return targetDate.toISOString().split('T')[0]!;
    }
  }

  return null;
}

function formatDayName(dateStr: string): string {
  const date = new Date(dateStr + 'T12:00:00');
  const today = new Date();
  today.setHours(12, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);

  if (date.toDateString() === today.toDateString()) return 'today';
  if (date.toDateString() === tomorrow.toDateString()) return 'tomorrow';

  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

function isDateInStayRange(dateStr: string, checkIn?: string, checkOut?: string): boolean {
  if (!checkIn || !checkOut) return true;
  // Compare only the date portion to avoid time-of-day false negatives
  // (e.g. checkIn "2026-02-22T06:57:32Z" has a time > midnight, so a
  // midnight-UTC Date object for today would compare as earlier than checkIn)
  const start = checkIn.split('T')[0]!;   // "2026-02-22"
  const end   = checkOut.split('T')[0]!;  // "2026-02-25"
  return dateStr >= start && dateStr <= end;
}

// =============================================================================
// COMPONENT PROPS
// =============================================================================

export interface GlobalVoiceOverlayProps {
  // Activation
  isActive: boolean;
  onClose: () => void;

  // Guest info
  guest?: ApiGuest | null;

  /** Phase D4.5.3: Current concierge view for context-aware voice commands */
  currentView?: 'grid' | 'service' | 'calendar' | 'room_service' | 'spa' | 'explore' | 'weather' | 'messages' | 'requests' | 'green-stay';

  // Navigation callbacks
  onOpenCalendar?: () => void;
  /** Phase D4.5.3: Open calendar on a specific day */
  onOpenCalendarDay?: (day: string) => void;
  onOpenBill?: () => void;
  onOpenMessages?: () => void;
  onOpenWeather?: () => void;
  onHome?: () => void;
  /** Phase P2.2-9: Open My Requests view */
  onOpenRequests?: () => void;

  // Phase D4.5.3: Calendar control callbacks (when calendar is open)
  onCalendarNextDay?: () => void;
  onCalendarPrevDay?: () => void;
  onCalendarGoToDay?: (day: string) => void;
  /** Phase D4.5.3: Voice-initiated booking cancellation - returns matched bookings and whether clarification is needed */
  onCalendarCancelBooking?: (bookingText?: string, dayRef?: string, timeRef?: string) => { matchedBookings: Booking[]; needsClarification: boolean };
  onCalendarBack?: () => void;
  /** Phase D4.5.3: Current bookings for voice cancellation matching */
  bookings?: Booking[];
  /** Phase D4.5.3: Voice confirmation of cancellation */
  onCalendarConfirmCancel?: () => void;
  onCalendarKeepBooking?: () => void;
  /** Phase D4.5.3: Whether cancel-confirm dialog is showing */
  isCancelConfirmShowing?: boolean;
  /** Select a booking to view its details */
  onCalendarSelectBooking?: (bookingText: string, dayRef?: string, bookingIndex?: number) => { matchedBookings: Booking[]; needsClarification: boolean };
  /** Navigate to the next booking in the current day's list */
  onCalendarNextBooking?: () => void;
  /** Navigate to the previous booking in the current day's list */
  onCalendarPrevBooking?: () => void;

  // Phase D4.5.4: Modify booking callbacks
  /** Phase D4.5.4: Voice-initiated booking modification - returns matched bookings and whether clarification is needed */
  onCalendarModifyBooking?: (bookingText?: string, dayRef?: string, timeRef?: string, newTime?: string, newDay?: string, newPartySize?: number) => { matchedBookings: Booking[]; needsClarification: boolean };
  /** Phase D4.5.4: Confirm pending modification */
  onCalendarConfirmModify?: () => void;
  /** Phase D4.5.4: Cancel pending modification */
  onCalendarCancelModify?: () => void;
  /** Phase D4.5.4: Set modification time only (slot completion) */
  onCalendarSetModifyTime?: (time: string) => void;
  /** Phase D4.5.4: Set modification day only (slot completion) */
  onCalendarSetModifyDay?: (day: string) => void;
  /** Phase D4.5.4: Set modification party size only (slot completion) */
  onCalendarSetModifyParty?: (partySize: number) => void;
  /** Phase D4.5.4: Whether modify-confirm dialog is showing */
  isModifyConfirmShowing?: boolean;

  // Zone switching
  onSwitchToSidebar?: () => void;
  onSwitchToConcierge?: () => void;
  onSwitchToTv?: () => void;

  // TV controls
  onTvChannel?: (channel: string) => void;
  onTvNextChannel?: () => void;
  onTvPrevChannel?: () => void;
  onTvFullscreen?: () => void;
  onTvExitFullscreen?: () => void;
  onTvMute?: () => void;
  onTvUnmute?: () => void;
  onTvOn?: () => void;
  onTvOff?: () => void;
  isTvFullscreen?: boolean;

  // Service navigation
  onOpenRoomService?: () => void;
  onOpenSpa?: () => void;
  onOpenExplore?: () => void;
  /** Phase P2.2-10: Open Green Stay view */
  onOpenGreenStay?: () => void;
  /** Phase D4.5.2: Open restaurant with optional booking slots */
  onOpenRestaurant?: (slots?: { time?: string; partySize?: number; transcript?: string }) => void;
  /** P2.7-2: Open Bar / Pool / Gym / Transport service cards */
  onOpenBar?: () => void;
  onOpenPool?: () => void;
  onOpenGym?: () => void;
  onOpenTransport?: () => void;
  /** P2.7-2: Book a specific option in Bar / Pool / Gym / Transport */
  onBookBar?: (option?: string) => void;
  onBookPool?: (option?: string) => void;
  onBookGym?: (option?: string) => void;
  onBookTransport?: (option?: string) => void;
  /** P2.7-2: True when BookingConfirmationModal is displayed — gates modal voice commands */
  bookingModalOpen?: boolean;
  /** P2.7-2: Booking confirmation modal voice command callbacks */
  onBookingModalSetDate?: (dateText: string) => void;
  onBookingModalSetTime?: (timeText: string) => void;
  onBookingModalConfirm?: () => void;
  onBookingModalCancel?: () => void;

  /** P2.9-1: Messages view synchronous command handler — returns response text for GlobalVoiceOverlay to speak (keeps TTS lifecycle intact) */
  onMessagesVoiceCommand?: (transcript: string) => string | null | undefined;

  // Phase P2.2-9: My Requests voice command callback
  /** Executes voice commands for MyRequestsView - returns TTS message */
  onRequestsVoiceCommand?: (command: {
    type: 'NEXT_COLUMN' | 'PREV_COLUMN' | 'GO_TO_COLUMN' | 'NEXT_REQUEST' | 'PREV_REQUEST' |
          'FIRST_REQUEST' | 'LAST_REQUEST' | 'GO_TO_INDEX' | 'EXPAND' | 'COLLAPSE' |
          'READ_REQUEST' | 'COUNT' | 'STATUS' | 'CANCEL_REQUEST' |
          'REPORT_FAULT' | 'CONFIRM_SUBMIT' | 'DISMISS_SUBMIT' | 'BACK';
    payload?: { column?: 'NEW' | 'IN_PROGRESS' | 'COMPLETED'; index?: number; faultId?: string };
  }) => string | void;

  // Phase P2.2-10: Green Stay voice command callback
  /** Executes voice commands for GreenStayView - returns TTS message */
  onGreenStayVoiceCommand?: (command:
    'ENABLE_ECO' | 'DISABLE_ECO' | 'SKIP_CLEANING_DATE' | 'TOWELS_ONLY_DATE' |
    'RESET_PREFERENCES' | 'SAVE_CHANGES' | 'DISCARD_CHANGES' | 'EXIT_ATTEMPT',
    date?: string
  ) => string;

  // Booking
  onBookingCreated?: (booking: CreateBookingRequest) => Promise<void>;

  // Green Stay
  greenStayEnabled?: boolean;
  onGreenStayEnable?: () => void;
  onGreenStayDisable?: () => void;
  skipCleaningDays?: string[];
  towelsOnlyDays?: string[];
  stayCheckIn?: string;
  stayCheckOut?: string;
  onSkipCleaningChange?: (days: string[]) => void;
  onTowelsOnlyChange?: (days: string[]) => void;

  // Phase D4.5.5: Demo Mode callbacks
  isDemoMode?: boolean;
  onDemoReset?: () => Promise<void>;
  onDemoRestartSession?: () => Promise<void>;
  onDemoClearBookings?: () => Promise<void>;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function GlobalVoiceOverlay({
  isActive,
  onClose,
  guest,
  currentView,
  onOpenCalendar,
  onOpenCalendarDay,
  onOpenBill,
  onOpenMessages,
  onOpenWeather,
  onHome,
  onOpenRequests,
  // Phase D4.5.3: Calendar control callbacks
  onCalendarNextDay,
  onCalendarPrevDay,
  onCalendarGoToDay,
  onCalendarCancelBooking,
  onCalendarBack,
  bookings: _bookings, // Used by onCalendarCancelBooking for matching
  onCalendarConfirmCancel,
  onCalendarKeepBooking,
  isCancelConfirmShowing,
  onCalendarSelectBooking,
  onCalendarNextBooking,
  onCalendarPrevBooking,
  // Phase D4.5.4: Modify booking callbacks
  onCalendarModifyBooking,
  onCalendarConfirmModify,
  onCalendarCancelModify,
  onCalendarSetModifyTime,
  onCalendarSetModifyDay,
  onCalendarSetModifyParty,
  isModifyConfirmShowing,
  onSwitchToSidebar,
  onSwitchToConcierge,
  onSwitchToTv,
  onTvChannel,
  onTvNextChannel,
  onTvPrevChannel,
  onTvFullscreen,
  onTvExitFullscreen,
  onTvMute,
  onTvUnmute,
  onTvOn,
  onTvOff,
  isTvFullscreen: _isTvFullscreen = false,
  onOpenRoomService,
  onOpenSpa,
  onOpenExplore,
  onOpenBar,
  onOpenPool,
  onOpenGym,
  onOpenTransport,
  onBookBar,
  onBookPool,
  onBookGym,
  onBookTransport,
  bookingModalOpen = false,
  onBookingModalSetDate,
  onBookingModalSetTime,
  onBookingModalConfirm,
  onBookingModalCancel,
  onOpenGreenStay,
  onMessagesVoiceCommand,
  onRequestsVoiceCommand,
  onGreenStayVoiceCommand,
  onOpenRestaurant,
  onBookingCreated: _onBookingCreated,
  greenStayEnabled = false,
  onGreenStayEnable,
  onGreenStayDisable,
  skipCleaningDays = [],
  towelsOnlyDays = [],
  stayCheckIn,
  stayCheckOut,
  onSkipCleaningChange,
  onTowelsOnlyChange,
  // Phase D4.5.5: Demo Mode
  isDemoMode = false,
  onDemoReset,
  onDemoRestartSession,
  onDemoClearBookings,
}: GlobalVoiceOverlayProps) {
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [transcript, setTranscript] = useState('');
  const [statusMessage, setStatusMessage] = useState('');

  const recognitionRef = useRef<ReturnType<typeof createSpeechRecognition>>(null);
  const transcriptRef = useRef('');
  const greenStayEnabledRef = useRef(greenStayEnabled);
  const bookingModalOpenRef = useRef(bookingModalOpen);
  const silenceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Stable refs to avoid stale closures in async recognition callbacks
  const currentViewRef = useRef(currentView);
  const voiceStateRef = useRef<VoiceState>('idle');
  const handleVoiceActionRef = useRef<(action: VoiceAction, transcript?: string) => void>(() => {});
  const startListeningRef = useRef<() => void>(() => {});

  const guestName = getGuestDisplayName(guest);
  const guestContext = useGuestContext();
  const { stay, room } = useData();

  // Phase D4.5.5: Demo mode failure tracking
  const { incrementFailures, resetFailures, consecutiveFailures, toFriendlyMessage } = useDemoMode();

  // Keep refs in sync
  useEffect(() => {
    greenStayEnabledRef.current = greenStayEnabled;
  }, [greenStayEnabled]);

  useEffect(() => {
    bookingModalOpenRef.current = bookingModalOpen;
  }, [bookingModalOpen]);

  useEffect(() => {
    transcriptRef.current = transcript;
  }, [transcript]);

  useEffect(() => {
    currentViewRef.current = currentView;
  }, [currentView]);

  useEffect(() => {
    voiceStateRef.current = voiceState;
  }, [voiceState]);

  // ==========================================================================
  // VOICE SYNTHESIS
  // ==========================================================================

  const speak = useCallback((text: string, onEnd?: () => void, opts?: { keepOpen?: boolean }) => {
    console.log('[GlobalVoice] Speaking:', text);

    // Abort any active recognition before TTS — prevents mic picking up speaker
    // audio and causing abort loops
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    setStatusMessage(text);
    zaxiaVoice.speak(text, {
      onStart: () => {
        setVoiceState('speaking');
      },
      onEnd: () => {
        console.log('[GlobalVoice] Speech ended');
        setVoiceState('idle');
        if (opts?.keepOpen) {
          // Keep the overlay open and immediately start listening again so the
          // user can voice-confirm without re-pressing the mic button.
          setTimeout(() => {
            startListeningRef.current();
          }, 300);
        } else {
          // Auto-close overlay after speaking
          setTimeout(() => {
            onClose();
          }, 500);
        }
        onEnd?.();
      },
      onError: (err) => {
        console.error('[GlobalVoice] Speech error:', err);
        setVoiceState('idle');
        onClose();
        onEnd?.();
      },
    });
  }, [onClose]);

  // ==========================================================================
  // SPEECH RECOGNITION
  // ==========================================================================

  const startListening = useCallback(() => {
    if (!isSpeechRecognitionAvailable()) {
      speak("I'm sorry, voice recognition isn't available in this browser.");
      return;
    }

    zaxiaVoice.cancel();

    // Abort any existing recognition
    if (recognitionRef.current) {
      recognitionRef.current.abort();
      recognitionRef.current = null;
    }

    const recognition = createSpeechRecognition();
    if (!recognition) return;

    recognitionRef.current = recognition;
    setVoiceState('listening');
    setTranscript('');
    setStatusMessage('Listening...');

    // Guard: each handler checks it's still the active instance before acting.
    // This prevents stale onerror/onend callbacks (e.g. from a speak()-aborted
    // instance) from firing after a newer recognition has already started.
    const isCurrent = () => recognitionRef.current === recognition;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
        if (!isCurrent()) return;

        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result?.[0]) {
            if (result.isFinal) {
              finalTranscript += result[0].transcript;
            } else {
              interimTranscript += result[0].transcript;
            }
          }
        }

        const currentTranscript = finalTranscript || interimTranscript;
        setTranscript(currentTranscript);
        transcriptRef.current = currentTranscript;

        // Phase D4.5.5: Reset silence timeout on any speech
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
        }
        silenceTimeoutRef.current = setTimeout(() => {
          if (isDemoMode && voiceStateRef.current === 'listening') {
            console.log('[GlobalVoice] Silence timeout in demo mode');
            speak("I didn't catch that. Try saying 'show my calendar' or 'book a spa treatment'.");
          }
        }, SILENCE_TIMEOUT_MS);
      };

      recognition.onerror = (event) => {
        // Ignore events from a superseded recognition instance
        if (!isCurrent()) return;

        console.error('[GlobalVoice] Recognition error:', event.error, event);

        // Don't close on 'aborted' (user interrupted) or 'no-speech' (silence)
        // These are expected when user presses spacebar to interrupt
        if (event.error === 'aborted') {
          // User interrupted - just reset state
          console.log('[GlobalVoice] Recognition aborted by user');
          setVoiceState('idle');
          return;
        }

        if (event.error === 'no-speech') {
          // Phase D4.5.5: In demo mode, offer helpful suggestions on silence
          if (isDemoMode) {
            speak("I'm still listening. Try saying something like 'show my calendar' or 'what's the weather'.");
            return;
          }
          setVoiceState('idle');
          return;
        }

        // Don't track permission errors as failures - these require user action
        if (event.error === 'not-allowed') {
          console.error('[GlobalVoice] Microphone permission denied - user needs to allow in browser');
          // Silently close - user will need to grant permission and try again
          setVoiceState('idle');
          onClose();
          return;
        }

        // Phase D4.5.5: Track actual recognition failures in demo mode (not user actions or permissions)
        console.log('[GlobalVoice] Tracking recognition failure:', event.error);
        if (isDemoMode) {
          incrementFailures();
          console.log('[GlobalVoice] Demo mode failure count:', consecutiveFailures + 1);

          // In demo mode, suggest fallback after repeated failures
          if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES - 1) {
            speak("I'm having a bit of trouble with voice right now. You can also use the remote to navigate.");
            setVoiceState('idle');
            onClose();
            return;
          }
        }

        // For other errors (network, etc.), close the overlay
        console.error('[GlobalVoice] Unhandled recognition error, closing overlay');
        setVoiceState('idle');
        onClose();
      };

      recognition.onend = () => {
        // Ignore end events from a superseded recognition instance
        if (!isCurrent()) return;

        // Recognition ended (may be auto or manual)
        // Clear silence timeout
        if (silenceTimeoutRef.current) {
          clearTimeout(silenceTimeoutRef.current);
          silenceTimeoutRef.current = null;
        }
        // If stuck in 'listening' state (e.g., network disconnect not caught by onerror),
        // reset to idle to prevent the UI from being frozen on the voice bar
        setVoiceState(prev => (prev === 'listening' ? 'idle' : prev));
      };

      try {
        recognition.start();
        console.log('[GlobalVoice] Recognition started successfully');
      } catch (error) {
        console.error('[GlobalVoice] Failed to start recognition:', error);
        setVoiceState('idle');
        onClose();
      }
  }, [speak, onClose, isDemoMode, incrementFailures, consecutiveFailures]);

  // Keep startListeningRef synced to the latest startListening function
  useEffect(() => {
    startListeningRef.current = startListening;
  }, [startListening]);

  const stopListeningAndProcess = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }

    // Clear silence timeout
    if (silenceTimeoutRef.current) {
      clearTimeout(silenceTimeoutRef.current);
      silenceTimeoutRef.current = null;
    }

    const currentTranscript = transcriptRef.current.trim();
    if (!currentTranscript) {
      // Phase D4.5.5: In demo mode, offer suggestions instead of just closing
      if (isDemoMode) {
        speak("I didn't catch that. Try 'show my calendar' or 'book a spa treatment'.");
        return;
      }
      setVoiceState('idle');
      onClose();
      return;
    }

    setVoiceState('processing');
    setStatusMessage('Processing...');

    // Phase D4.5.5: Reset failure count on successful speech capture
    resetFailures();

    // Process the voice command via the central voice engine
    // Phase D4.5.3: Use actual current view for context-aware commands (e.g., cancel confirmation)
    // Phase P2.2-9: Map currentView to VoiceContext activeView
    const viewMap: Record<string, 'grid' | 'room_service' | 'spa' | 'explore' | 'calendar' | 'requests' | 'tv' | 'overlay'> = {
      'grid': 'grid',
      'calendar': 'calendar',
      'requests': 'requests',
      'green-stay': 'overlay',  // Green Stay uses overlay voice context (no dedicated view type)
      'messages': 'overlay',    // Messages view is intercepted before engine; engine context doesn't matter
      'room_service': 'room_service',
      'spa': 'spa',
      'explore': 'explore',
      'tv': 'tv',
    };
    const effectiveView = viewMap[currentViewRef.current || 'grid'] || 'overlay';
    const voiceContext = createVoiceContext(effectiveView, {
      ecoModeEnabled: greenStayEnabledRef.current,
      subContext: {
        bookingModalOpen: bookingModalOpenRef.current,
      },
    });
    console.log('[GlobalVoice] Processing:', currentTranscript, 'view:', effectiveView, 'cancelConfirm:', isCancelConfirmShowing, 'modifyConfirm:', isModifyConfirmShowing);
    const action = processVoiceInput(currentTranscript, voiceContext);
    console.log('[GlobalVoice] Action:', action.type);

    // When Messages view is active, call synchronous handler and speak response via overlay.
    // No keepOpen — overlay closes after each command so the mic doesn't auto-restart;
    // user presses spacebar again for the next command (consistent with calendar day-nav).
    if (currentViewRef.current === 'messages' && onMessagesVoiceCommand) {
      const response = onMessagesVoiceCommand(currentTranscript);
      if (response) {
        speak(response);
      }
      return;
    }

    if (action.type !== 'UNKNOWN') {
      handleVoiceActionRef.current(action, currentTranscript);
    } else {
      // On green-stay view, give a contextual hint immediately instead of slow concierge call
      if (currentViewRef.current === 'green-stay') {
        speak(
          "I can help with your Green Stay preferences. Try 'skip cleaning tomorrow', 'towels only on Monday', 'save changes', or 'go back'.",
          undefined,
          { keepOpen: true }
        );
        return;
      }

      // No recognized command - try concierge
      askConcierge(ConciergeIntent.RECOMMENDATIONS, new Date(), guestContext ? {
        guestName: guestContext.guestNames.split(' ')[0],
        stayDay: guestContext.stayDay,
        isCheckoutDay: guestContext.isCheckoutDay,
        isFirstDay: guestContext.isFirstDay,
        todayBookingServiceIds: guestContext.todayBookings.map(b => b.serviceId),
        timeOfDay: guestContext.timeOfDay,
      } : undefined)
        .then(result => {
          speak(result.spokenResponse);
        })
        .catch(() => {
          // Phase D4.5.5: Use friendly message in demo mode
          const errorMsg = isDemoMode
            ? toFriendlyMessage(`I didn't understand that`)
            : `I'm sorry ${guestName}, I didn't understand that. Could you try again?`;
          speak(errorMsg);
        });
    }
  }, [guestName, guestContext, onClose, isDemoMode, resetFailures, toFriendlyMessage]);

  // ==========================================================================
  // VOICE ACTION HANDLER
  // ==========================================================================

  // Helper to create a guest request via API
  const submitGuestRequest = useCallback(async (category: GuestRequestCategory, description: string) => {
    if (!stay || !room || !guest) {
      console.error('[GlobalVoice] Cannot create request - missing stay/room/guest data');
      return false;
    }

    try {
      await createGuestRequest({
        stayId: stay.stayId,
        roomNumber: room.roomNumber,
        guestName: `${guest.firstName} ${guest.lastName}`.trim(),
        category,
        description,
      });
      console.log('[GlobalVoice] Guest request created:', category, description);
      return true;
    } catch (error) {
      console.error('[GlobalVoice] Failed to create guest request:', error);
      return false;
    }
  }, [stay, room, guest]);

  const handleVoiceAction = useCallback((action: VoiceAction, transcript?: string) => {
    switch (action.type) {
      case 'OPEN_CALENDAR':
        speak("Opening your calendar.", () => onOpenCalendar?.());
        break;

      // Phase D4.5.3: Open calendar on specific day
      case 'OPEN_CALENDAR_DAY': {
        const dayStr = parseDayReference(action.payload.day);
        if (dayStr && isDateInStayRange(dayStr, stayCheckIn, stayCheckOut)) {
          const dayName = formatDayName(dayStr);
          speak(`Showing ${dayName}'s schedule.`, () => onOpenCalendarDay?.(dayStr));
        } else if (dayStr) {
          speak("That day is outside your stay.");
        } else {
          speak("Opening your calendar.", () => onOpenCalendar?.());
        }
        break;
      }

      // Phase D4.5.3: Calendar navigation (when calendar is open)
      case 'CALENDAR_NEXT_DAY':
        speak("Next day.", () => onCalendarNextDay?.());
        break;

      case 'CALENDAR_PREV_DAY':
        speak("Previous day.", () => onCalendarPrevDay?.());
        break;

      case 'CALENDAR_GO_TO_DAY': {
        const dayStr = parseDayReference(action.payload.day);
        if (dayStr && isDateInStayRange(dayStr, stayCheckIn, stayCheckOut)) {
          const dayName = formatDayName(dayStr);
          speak(`Going to ${dayName}.`, () => onCalendarGoToDay?.(dayStr));
        } else if (dayStr) {
          speak("That day is outside your stay.");
        } else {
          speak("I didn't catch which day.");
        }
        break;
      }

      case 'CALENDAR_CANCEL_BOOKING': {
        const bookingText = action.payload?.bookingText || '';
        const dayRef = action.payload?.dayRef;
        const timeRef = action.payload?.timeRef;
        console.log('[GlobalVoice] Cancel booking - extracted:', { bookingText, dayRef, timeRef });

        if (!onCalendarCancelBooking) {
          speak("I can't cancel bookings right now.");
          break;
        }

        // Try to find matching bookings
        const result = onCalendarCancelBooking(bookingText, dayRef, timeRef);

        if (result.matchedBookings.length === 0) {
          if (bookingText) {
            speak(`I couldn't find any ${bookingText} bookings to cancel.`);
          } else {
            speak("I couldn't find any bookings to cancel. Try saying 'cancel my spa booking' or 'cancel the massage tomorrow'.");
          }
        } else if (result.needsClarification) {
          // Multiple matches - ask for clarification
          const bookingList = result.matchedBookings.map(b => {
            const date = new Date(b.datetime);
            const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
            const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            return `${b.serviceName} on ${dayName} at ${time}`;
          }).join(', ');

          speak(`I found ${result.matchedBookings.length} bookings. Which would you like to cancel: ${bookingList}? Try being more specific, like 'cancel the spa tomorrow'.`);
        } else {
          // Single match - calendar will show the booking with cancel confirmation.
          // Keep the overlay open so the user can confirm by voice.
          const booking = result.matchedBookings[0]!;
          speak(
            `Found your ${booking.serviceName} ${booking.category === 'ROOM_SERVICE' ? 'order' : 'booking'}. Say 'yes' or 'confirm' to cancel, or 'keep' to keep it.`,
            undefined,
            { keepOpen: true },
          );
        }
        break;
      }

      case 'CALENDAR_SELECT_BOOKING': {
        const bookingText = action.payload?.bookingText || '';
        const dayRef = action.payload?.dayRef;
        const bookingIndex = action.payload?.bookingIndex;
        console.log('[GlobalVoice] Select booking - extracted:', { bookingText, dayRef, bookingIndex });

        if (!onCalendarSelectBooking) {
          speak("I can't select bookings right now.");
          break;
        }

        // Try to find matching bookings
        const result = onCalendarSelectBooking(bookingText, dayRef, bookingIndex);

        if (result.matchedBookings.length === 0) {
          if (bookingText) {
            speak(`I couldn't find any ${bookingText} bookings.`);
          } else if (dayRef) {
            speak(`I couldn't find any bookings for ${dayRef}.`);
          } else {
            speak("I couldn't find any bookings. Try saying 'show my spa booking' or 'select my dinner reservation'.");
          }
        } else if (result.needsClarification) {
          // Multiple matches - ask for clarification
          const bookingList = result.matchedBookings.map(b => {
            const date = new Date(b.datetime);
            const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
            const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            return `${b.serviceName} on ${dayName} at ${time}`;
          }).join(', ');

          speak(`I found ${result.matchedBookings.length} bookings: ${bookingList}. Which would you like to see? Try being more specific, like 'show my spa booking tomorrow'.`);
        } else {
          // Single match - calendar will show the booking details
          const booking = result.matchedBookings[0]!;
          const bookingOrOrder = booking.category === 'ROOM_SERVICE' ? 'order' : 'booking';
          speak(`Here's your ${booking.serviceName} ${bookingOrOrder}.`);
        }
        break;
      }

      case 'CALENDAR_NEXT_BOOKING':
        speak("Next booking.", () => onCalendarNextBooking?.());
        break;

      case 'CALENDAR_PREV_BOOKING':
        speak("Previous booking.", () => onCalendarPrevBooking?.());
        break;

      case 'CALENDAR_CONFIRM_CANCEL':
        // User said "yes" or "confirm" - check which dialog is actually showing
        // Phase D4.5.4: If modify dialog is showing, treat as modify confirmation
        if (isModifyConfirmShowing && onCalendarConfirmModify) {
          speak("Updating your booking.", () => onCalendarConfirmModify());
        } else if (isCancelConfirmShowing && onCalendarConfirmCancel) {
          speak("Cancelling the booking.", () => onCalendarConfirmCancel());
        } else {
          speak("Please use the screen to confirm.");
        }
        break;

      case 'CALENDAR_KEEP_BOOKING':
        // User said "no" or "keep it" - check which dialog is actually showing
        // Phase D4.5.4: If modify dialog is showing, treat as cancel modification
        if (isModifyConfirmShowing && onCalendarCancelModify) {
          speak("Okay, keeping your original booking.", () => onCalendarCancelModify());
        } else if (onCalendarKeepBooking) {
          speak("Okay, keeping the booking.", () => onCalendarKeepBooking());
        } else {
          speak("Okay, keeping the booking.");
        }
        break;

      // ========================================================================
      // Phase D4.5.4: MODIFY BOOKING HANDLERS
      // ========================================================================

      case 'CALENDAR_MODIFY_BOOKING': {
        const bookingText = action.payload?.bookingText || '';
        const dayRef = action.payload?.dayRef;
        const timeRef = action.payload?.timeRef;
        const newTime = action.payload?.newTime;
        const newDay = action.payload?.newDay;
        const newPartySize = action.payload?.newPartySize;
        console.log('[GlobalVoice] Modify booking - extracted:', { bookingText, dayRef, timeRef, newTime, newDay, newPartySize });

        if (!onCalendarModifyBooking) {
          speak("I can't modify bookings right now.");
          break;
        }

        // Try to find matching bookings
        const result = onCalendarModifyBooking(bookingText, dayRef, timeRef, newTime, newDay, newPartySize);

        if (result.matchedBookings.length === 0) {
          if (bookingText) {
            speak(`I couldn't find any ${bookingText} bookings to modify.`);
          } else {
            speak("I couldn't find any bookings to modify. Try saying 'change my spa to tomorrow at 6' or 'move my dinner to 8pm'.");
          }
        } else if (result.needsClarification) {
          // Multiple matches - ask for clarification
          const bookingList = result.matchedBookings.map(b => {
            const date = new Date(b.datetime);
            const dayName = date.toLocaleDateString('en-US', { weekday: 'long' });
            const time = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            return `${b.serviceName} on ${dayName} at ${time}`;
          }).join(', ');

          speak(`I found ${result.matchedBookings.length} bookings. Which would you like to modify: ${bookingList}? Try being more specific, like 'change my spa tomorrow'.`);
        } else {
          // Single match - calendar will show modification preview
          const booking = result.matchedBookings[0]!;
          speak(`Found your ${booking.serviceName} booking. Please confirm the change on screen.`);
        }
        break;
      }

      case 'CALENDAR_CONFIRM_MODIFY':
        // User said "yes" or "confirm" - check which dialog is actually showing
        // Could also be triggered when cancel dialog is showing
        if (isModifyConfirmShowing && onCalendarConfirmModify) {
          speak("Updating your booking.", () => onCalendarConfirmModify());
        } else if (isCancelConfirmShowing && onCalendarConfirmCancel) {
          speak("Cancelling the booking.", () => onCalendarConfirmCancel());
        } else {
          speak("Please use the screen to confirm.");
        }
        break;

      case 'CALENDAR_CANCEL_MODIFY':
        // User said "no" or "nevermind" - check which dialog is showing
        if (isModifyConfirmShowing && onCalendarCancelModify) {
          speak("Okay, keeping your original booking.", () => onCalendarCancelModify());
        } else if (isCancelConfirmShowing && onCalendarKeepBooking) {
          speak("Okay, keeping the booking.", () => onCalendarKeepBooking());
        } else {
          speak("Okay, no changes made.");
        }
        break;

      case 'CALENDAR_SET_MODIFY_TIME': {
        // Slot completion: user provided just the time
        const newTime = action.payload?.time;
        if (newTime && onCalendarSetModifyTime) {
          speak(`Got it, ${newTime}.`, () => onCalendarSetModifyTime(newTime));
        } else {
          speak("I didn't catch the time.");
        }
        break;
      }

      case 'CALENDAR_SET_MODIFY_DATE': {
        // Slot completion: user provided just the day
        const dayRef = action.payload?.day;
        if (dayRef) {
          const dayStr = parseDayReference(dayRef);
          if (dayStr && isDateInStayRange(dayStr, stayCheckIn, stayCheckOut) && onCalendarSetModifyDay) {
            const dayName = formatDayName(dayStr);
            speak(`Got it, ${dayName}.`, () => onCalendarSetModifyDay(dayStr));
          } else if (dayStr) {
            speak("That day is outside your stay.");
          } else {
            speak("I didn't catch which day.");
          }
        } else {
          speak("I didn't catch the day.");
        }
        break;
      }

      case 'CALENDAR_SET_MODIFY_PARTY': {
        // Slot completion: user provided party size
        const partySize = action.payload?.partySize;
        if (partySize && partySize >= 1 && partySize <= 20 && onCalendarSetModifyParty) {
          speak(`Got it, party of ${partySize}.`, () => onCalendarSetModifyParty(partySize));
        } else if (partySize) {
          speak("Party size must be between 1 and 20.");
        } else {
          speak("I didn't catch the party size.");
        }
        break;
      }

      case 'CALENDAR_BACK':
        speak("Going back.", () => onCalendarBack?.());
        break;

      case 'OPEN_BILL':
        speak("Here's your bill.", () => onOpenBill?.());
        break;

      case 'REQUESTS_OPEN':
        speak("Opening My Room.", () => onOpenRequests?.());
        break;

      case 'OPEN_MESSAGES':
        speak("Opening your messages.", () => onOpenMessages?.());
        break;

      case 'OPEN_WEATHER':
        speak("Showing the weather.", () => onOpenWeather?.());
        break;

      case 'HOME':
      case 'BACK':
        // Phase P2.2-10: Check if Green Stay has unsaved changes
        console.log('[GlobalVoice] HOME/BACK command - currentView:', currentView, 'onGreenStayVoiceCommand:', !!onGreenStayVoiceCommand);
        if (currentView === 'green-stay' && onGreenStayVoiceCommand) {
          console.log('[GlobalVoice] Calling Green Stay EXIT_ATTEMPT handler');
          // Trigger exit attempt via Green Stay's handler - it will show modal if needed
          const response = onGreenStayVoiceCommand('EXIT_ATTEMPT');
          console.log('[GlobalVoice] Green Stay response:', response);
          if (response === 'EXIT_BLOCKED') {
            // Exit blocked - modal will handle user choice
            // Reset voice state and close overlay since command cannot proceed
            setVoiceState('idle');
            onClose();
            break;
          }
        }
        // Only navigate if not blocked
        if (currentView === 'grid') {
          speak("You're already on the home screen.");
        } else {
          speak("Going back.", () => onHome?.());
        }
        break;

      case 'CLOSE':
        speak("Goodbye!");
        break;

      case 'ZONE_SIDEBAR':
        speak("Switching to sidebar.", () => onSwitchToSidebar?.());
        break;

      case 'ZONE_CONCIERGE':
        speak("Switching to concierge.", () => onSwitchToConcierge?.());
        break;

      case 'ZONE_TV':
        speak("Switching to TV.", () => onSwitchToTv?.());
        break;

      case 'TV_CHANNEL':
        speak(`Switching to ${action.payload.channel}.`, () => onTvChannel?.(action.payload.channel));
        break;

      case 'TV_NEXT_CHANNEL':
        speak("Next channel.", () => onTvNextChannel?.());
        break;

      case 'TV_PREV_CHANNEL':
        speak("Previous channel.", () => onTvPrevChannel?.());
        break;

      case 'TV_FULLSCREEN':
        speak("Going fullscreen.", () => onTvFullscreen?.());
        break;

      case 'TV_EXIT_FULLSCREEN':
        speak("Exiting fullscreen.", () => onTvExitFullscreen?.());
        break;

      case 'TV_MUTE':
        speak("Muting TV.", () => onTvMute?.());
        break;

      case 'TV_UNMUTE':
        speak("Unmuting TV.", () => onTvUnmute?.());
        break;

      case 'TV_ON':
        speak("Turning the TV on.", () => onTvOn?.());
        break;

      case 'TV_OFF':
        speak("Turning the TV off.", () => onTvOff?.());
        break;

      case 'OPEN_ROOM_SERVICE':
        speak("Opening room service.", () => onOpenRoomService?.());
        break;

      case 'OPEN_RESTAURANT':
        if (onOpenRestaurant) {
          speak("Opening the restaurant.", () => onOpenRestaurant!({}));
        } else {
          speak("Restaurant is currently unavailable.");
        }
        break;

      case 'OPEN_SPA':
        speak("Opening the spa.", () => onOpenSpa?.());
        break;

      case 'OPEN_EXPLORE':
        speak("Let me show you what's nearby.", () => onOpenExplore?.());
        break;

      case 'OPEN_GREEN_STAY':
        speak("Opening your Green Stay preferences.", () => onOpenGreenStay?.());
        break;

      case 'OPEN_BAR':
        speak("Opening the bar.", () => onOpenBar?.());
        break;

      case 'OPEN_POOL':
        speak("Opening the pool.", () => onOpenPool?.());
        break;

      case 'OPEN_GYM':
        speak("Opening the gym.", () => onOpenGym?.());
        break;

      case 'OPEN_TRANSPORT':
        speak("Opening transport services.", () => onOpenTransport?.());
        break;

      case 'BOOK_BAR':
        speak("Opening the bar. I'll pre-select that option for you.", () => onBookBar?.(action.payload?.option));
        break;

      case 'BOOK_POOL':
        speak("Opening the pool. I'll pre-select that option for you.", () => onBookPool?.(action.payload?.option));
        break;

      case 'BOOK_GYM':
        speak("Opening the gym. I'll pre-select that option for you.", () => onBookGym?.(action.payload?.option));
        break;

      case 'BOOK_TRANSPORT':
        speak("Opening transport services. I'll pre-select that option for you.", () => onBookTransport?.(action.payload?.option));
        break;

      // -----------------------------------------------------------------------
      // Booking confirmation modal controls (P2.7-2)
      // All cases are no-ops when bookingModalOpen is false.
      // -----------------------------------------------------------------------
      case 'BOOKING_MODAL_SET_DATE':
        if (bookingModalOpen) {
          speak(`Date set to ${action.payload.dateText}.`, () => onBookingModalSetDate?.(action.payload.dateText));
        } else {
          // Modal not open — nothing to update; reset to idle to avoid stuck state.
          setVoiceState('idle');
        }
        break;

      case 'BOOKING_MODAL_SET_TIME':
        if (bookingModalOpen) {
          speak(`Time set to ${action.payload.timeText}.`, () => onBookingModalSetTime?.(action.payload.timeText));
        } else {
          setVoiceState('idle');
        }
        break;

      case 'BOOKING_MODAL_CONFIRM':
        if (bookingModalOpen) {
          speak('Confirming your booking.', () => onBookingModalConfirm?.());
        } else {
          setVoiceState('idle');
        }
        break;

      case 'BOOKING_MODAL_CANCEL':
        // When the cancel-confirm dialog is showing, a bare "cancel" (which
        // routes here at priority 8) should be treated as voice confirmation
        // of the cancellation — not as a booking-modal dismiss.
        if (isCancelConfirmShowing && onCalendarConfirmCancel) {
          speak('Cancelling the booking.', () => onCalendarConfirmCancel());
        } else if (isModifyConfirmShowing && onCalendarCancelModify) {
          speak('Okay, keeping your original booking.', () => onCalendarCancelModify());
        } else if (bookingModalOpen) {
          speak('Booking cancelled.', () => onBookingModalCancel?.());
        } else {
          // No modal open — nothing to cancel
          speak("Nothing to cancel right now.");
        }
        break;

      // Handle contextual CANCEL (may be booking cancel)
      case 'CANCEL':
        // If cancel-confirm dialog is showing, "cancel" means the user is confirming they want to cancel
        if (isCancelConfirmShowing && onCalendarConfirmCancel) {
          speak("Cancelling.", () => onCalendarConfirmCancel());
        } else {
          speak("Cancelled.");
        }
        break;
      case 'GREEN_STAY_ASK': {
        const isEnabled = greenStayEnabledRef.current;
        const explanation = isEnabled
          ? "Green Stay is already enabled for your stay. I'll continue suggesting greener options when it makes sense."
          : "Green Stay lets you enjoy your stay while reducing unnecessary waste. For example, skipping daily cleaning can save around 60 liters of water per day. Would you like me to enable Green Stay for your stay?";
        speak(explanation);
        break;
      }

      case 'GREEN_STAY_ENABLE': {
        // Phase P2.2-10: Delegate to view-specific handler if already on Green Stay
        if (currentView === 'green-stay' && onGreenStayVoiceCommand) {
          const response = onGreenStayVoiceCommand('ENABLE_ECO');
          speak(response, undefined, { keepOpen: true });
          break;
        }

        // Not on green-stay view — enable directly and navigate
        if (greenStayEnabledRef.current) {
          speak("Green Stay is already enabled.");
        } else {
          greenStayEnabledRef.current = true;
          onGreenStayEnable?.();
          onOpenGreenStay?.();
          speak("Green Stay has been enabled! Opening your preferences so you can customise your choices.");
        }
        break;
      }

      case 'GREEN_STAY_DISABLE': {
        // Phase P2.2-10: Delegate to view-specific handler if available
        if (currentView === 'green-stay' && onGreenStayVoiceCommand) {
          const response = onGreenStayVoiceCommand('DISABLE_ECO');
          speak(response, undefined, { keepOpen: true });
          break;
        }

        if (!greenStayEnabledRef.current) {
          speak("Green Stay is not currently enabled.");
        } else {
          greenStayEnabledRef.current = false;
          onSkipCleaningChange?.([]);
          onTowelsOnlyChange?.([]);
          onGreenStayDisable?.();
          speak("Green Stay has been disabled. You can enable it again anytime.");
        }
        break;
      }

      case 'GREEN_STAY_SKIP_CLEANING': {
        const targetDate = parseDayReference(action.payload.day);
        if (!targetDate) {
          speak("I couldn't understand which day you mean. Please try again with a specific day like tomorrow or Monday.");
          break;
        }
        if (!isDateInStayRange(targetDate, stayCheckIn, stayCheckOut)) {
          speak("That date is outside your stay. Please choose a day during your visit.");
          break;
        }

        // Delegate to view-specific handler (pending state) when on green-stay view
        if (currentView === 'green-stay' && onGreenStayVoiceCommand) {
          const response = onGreenStayVoiceCommand('SKIP_CLEANING_DATE', targetDate);
          speak(response, undefined, { keepOpen: true });
          break;
        }

        // Fallback: direct state-update when not on green-stay view
        if (!greenStayEnabledRef.current) {
          greenStayEnabledRef.current = true;
          onGreenStayEnable?.();
        }
        const newDays = skipCleaningDays.includes(targetDate)
          ? skipCleaningDays
          : [...skipCleaningDays, targetDate];
        onSkipCleaningChange?.(newDays);
        const dayName = formatDayName(targetDate);
        speak(`Got it. I've scheduled to skip cleaning on ${dayName}.`);
        break;
      }

      case 'GREEN_STAY_TOWELS_ONLY': {
        const targetDate = parseDayReference(action.payload.day);
        if (!targetDate) {
          speak("I couldn't understand which day you mean. Please try again with a specific day.");
          break;
        }
        if (!isDateInStayRange(targetDate, stayCheckIn, stayCheckOut)) {
          speak("That date is outside your stay. Please choose a day during your visit.");
          break;
        }

        // Delegate to view-specific handler (pending state) when on green-stay view
        if (currentView === 'green-stay' && onGreenStayVoiceCommand) {
          const response = onGreenStayVoiceCommand('TOWELS_ONLY_DATE', targetDate);
          speak(response, undefined, { keepOpen: true });
          break;
        }

        // Fallback: direct state-update when not on green-stay view
        if (!greenStayEnabledRef.current) {
          greenStayEnabledRef.current = true;
          onGreenStayEnable?.();
        }
        const isAdding = !towelsOnlyDays.includes(targetDate);
        const newDays = isAdding
          ? [...towelsOnlyDays, targetDate]
          : towelsOnlyDays.filter(d => d !== targetDate);
        onTowelsOnlyChange?.(newDays);

        if (isAdding) {
          // Auto-add skip cleaning since towels only implies skipping full cleaning
          if (!skipCleaningDays.includes(targetDate)) {
            onSkipCleaningChange?.([...skipCleaningDays, targetDate]);
          }
        } else {
          // Remove skip cleaning when towels only is removed
          onSkipCleaningChange?.(skipCleaningDays.filter(d => d !== targetDate));
        }

        const dayName = formatDayName(targetDate);
        speak(isAdding
          ? `Got it. I've scheduled towels only on ${dayName}.`
          : `Removed towels only for ${dayName}.`
        );
        break;
      }

      case 'GREEN_STAY_SHOW': {
        const skipCount = skipCleaningDays.length;
        const towelsCount = towelsOnlyDays.length;
        if (skipCount === 0 && towelsCount === 0) {
          speak("You don't have any green stay preferences scheduled yet.");
        } else {
          let response = "Your green stay preferences: ";
          if (skipCount > 0) {
            const skipDayNames = skipCleaningDays.map(formatDayName).join(', ');
            response += `Skip cleaning on ${skipDayNames}. `;
          }
          if (towelsCount > 0) {
            const towelDayNames = towelsOnlyDays.map(formatDayName).join(', ');
            response += `Towels only on ${towelDayNames}.`;
          }
          speak(response);
        }
        break;
      }

      case 'GREEN_STAY_CLEAR': {
        // Phase P2.2-10: Delegate to view-specific handler to reset pending state in GreenStayView
        if (currentView === 'green-stay' && onGreenStayVoiceCommand) {
          const response = onGreenStayVoiceCommand('RESET_PREFERENCES');
          speak(response !== 'Voice command not recognized.' ? response : "I've cleared all your green stay preferences.", undefined, { keepOpen: true });
          break;
        }

        if (skipCleaningDays.length === 0 && towelsOnlyDays.length === 0) {
          speak("You don't have any green stay preferences to clear.");
        } else {
          onSkipCleaningChange?.([]);
          onTowelsOnlyChange?.([]);
          speak("I've cleared all your green stay preferences.");
        }
        break;
      }

      // Phase P2.2-10: Bulk Green Stay operations
      case 'GREEN_STAY_SELECT_ALL_SKIP': {
        // Bulk-select all days — not directly expressible via a single voice command string;
        // instruct the guest to use the Green Stay page controls instead.
        speak("Please use the Green Stay page to select all days. Open it by saying 'open green stay'.");
        break;
      }

      case 'GREEN_STAY_SELECT_ALL_TOWELS': {
        speak("Please use the Green Stay page to select all days. Open it by saying 'open green stay'.");
        break;
      }

      case 'GREEN_STAY_CLEAR_SKIP': {
        onSkipCleaningChange?.([]);
        speak("I've cleared all skip cleaning selections.");
        break;
      }

      case 'GREEN_STAY_CLEAR_TOWELS': {
        onTowelsOnlyChange?.([]);
        speak("I've cleared all towels only selections.");
        break;
      }

      case 'GREEN_STAY_READ_SELECTIONS': {
        {
          const skipCount = skipCleaningDays.length;
          const towelsCount = towelsOnlyDays.length;
          if (skipCount === 0 && towelsCount === 0) {
            speak("You have no green stay selections yet.");
          } else {
            let response = "Your selections: ";
            if (skipCount > 0) {
              response += `${skipCount} ${skipCount === 1 ? 'day' : 'days'} with cleaning skipped. `;
            }
            if (towelsCount > 0) {
              response += `${towelsCount} ${towelsCount === 1 ? 'day' : 'days'} with towels only.`;
            }
            speak(response);
          }
        }
        break;
      }

      case 'SHOW_IMPACT': {
        // Phase P2.2-10: Show environmental impact savings
        speak("Please open Green Stay to see your environmental impact by saying 'open green stay'.");
        break;
      }

      case 'SAVE_CHANGES': {
        // Phase P2.2-10: Save changes in Green Stay view
        if (currentView === 'green-stay' && onGreenStayVoiceCommand) {
          const response = onGreenStayVoiceCommand('SAVE_CHANGES');
          speak(response);
        } else {
          speak("There are no pending changes to save. Open Green Stay to make changes.");
        }
        break;
      }

      case 'DISCARD_CHANGES': {
        // Phase P2.2-10: Discard changes in Green Stay view
        if (currentView === 'green-stay' && onGreenStayVoiceCommand) {
          const response = onGreenStayVoiceCommand('DISCARD_CHANGES');
          speak(response);
        } else {
          speak("There are no pending changes to discard.");
        }
        break;
      }

      case 'TRANSPORT_REQUEST': {
        const description = transcript || 'Transport request';
        submitGuestRequest(GuestRequestCategory.CONCIERGE, description)
          .then(success => {
            if (success) {
              if (greenStayEnabledRef.current) {
                speak("I've notified the concierge. Since you're on Green Stay, they'll suggest eco-friendly options like walking or bicycles.");
              } else {
                speak("I've notified the team. Someone will contact you shortly to arrange transport.");
              }
            } else {
              speak("I'm sorry, I couldn't submit your request right now. Please try again or use the phone.");
            }
          });
        break;
      }

      case 'MAINTENANCE_REQUEST': {
        const description = transcript || 'Maintenance request';
        submitGuestRequest(GuestRequestCategory.MAINTENANCE, description)
          .then(success => {
            if (success) {
              speak("I've notified maintenance. Someone will assist you shortly.");
            } else {
              speak("I'm sorry, I couldn't submit your request right now. Please try again or use the phone.");
            }
          });
        break;
      }

      case 'SHOW_PROMOTIONS':
        speak("Today's special offers are displayed on the TV screen.");
        break;

      case 'BOOK_RESTAURANT':
        // Phase D4.5.2: Navigate to restaurant with booking slots
        if (onOpenRestaurant) {
          const slots = action.payload?.transcript
            ? { transcript: action.payload.transcript }
            : {};
          speak("Opening the restaurant.", () => onOpenRestaurant(slots));
        } else {
          speak("Restaurant booking is currently unavailable.");
        }
        break;

      case 'CONCIERGE_INTENT':
        // For complex intents, use the concierge API
        askConcierge(
          action.payload.intent,
          new Date(),
          guestContext ? {
            guestName: guestContext.guestNames.split(' ')[0],
            stayDay: guestContext.stayDay,
            isCheckoutDay: guestContext.isCheckoutDay,
            isFirstDay: guestContext.isFirstDay,
            todayBookingServiceIds: guestContext.todayBookings.map(b => b.serviceId),
            timeOfDay: guestContext.timeOfDay,
          } : undefined
        )
          .then(result => {
            speak(result.spokenResponse);
          })
          .catch(() => {
            speak(`I'm sorry ${guestName}, I had trouble with that request.`);
          });
        break;

      // ========================================================================
      // DEMO MODE COMMANDS (Phase D4.5.5)
      // ========================================================================
      case 'DEMO_RESET':
        if (!isDemoMode) {
          speak("I don't understand that command.");
          break;
        }
        if (onDemoReset) {
          speak("Resetting the demo. One moment please.", () => {
            onDemoReset().then(() => {
              speak("Demo has been reset. All bookings cleared.");
            }).catch(() => {
              speak("I had a small issue resetting — let me try again.");
            });
          });
        } else {
          speak("Demo reset is not available right now.");
        }
        break;

      case 'DEMO_RESTART_SESSION':
        if (!isDemoMode) {
          speak("I don't understand that command.");
          break;
        }
        if (onDemoRestartSession) {
          speak("Restarting the session. This will reload the display.", () => {
            onDemoRestartSession().then(() => {
              // The page will reload, so this might not be spoken
              speak("Session restarted.");
            }).catch(() => {
              speak("I had a small issue — let me try again.");
            });
          });
        } else {
          speak("Session restart is not available right now.");
        }
        break;

      case 'DEMO_CLEAR_BOOKINGS':
        if (!isDemoMode) {
          speak("I don't understand that command.");
          break;
        }
        if (onDemoClearBookings) {
          speak("Clearing all bookings.", () => {
            onDemoClearBookings().then(() => {
              speak("All bookings have been cleared. Your calendar is now empty.");
            }).catch(() => {
              speak("I had a small issue clearing bookings — let me try again.");
            });
          });
        } else {
          speak("Clearing bookings is not available right now.");
        }
        break;

      case 'DEMO_USE_AZURE_VOICE':
        if (!isDemoMode) {
          speak("I don't understand that command.");
          break;
        }
        zaxiaVoice.setVoiceMode('azure');
        speak("Switched to premium Azure voice. This provides the best quality but incurs API costs.");
        break;

      case 'DEMO_USE_FREE_VOICE':
        if (!isDemoMode) {
          speak("I don't understand that command.");
          break;
        }
        zaxiaVoice.setVoiceMode('browser');
        speak("Switched to free browser voice. This saves API costs during testing.");
        break;

      // ==========================================================================
      // MY REQUESTS ACTIONS (Phase P2.2-9)
      // ==========================================================================
      case 'REQUESTS_NEXT_COLUMN':
      case 'REQUESTS_PREV_COLUMN':
      case 'REQUESTS_GO_TO_COLUMN':
      case 'REQUESTS_NEXT_REQUEST':
      case 'REQUESTS_PREV_REQUEST':
      case 'REQUESTS_FIRST_REQUEST':
      case 'REQUESTS_LAST_REQUEST':
      case 'REQUESTS_GO_TO_INDEX':
      case 'REQUESTS_EXPAND':
      case 'REQUESTS_COLLAPSE':
      case 'REQUESTS_READ_REQUEST':
      case 'REQUESTS_COUNT':
      case 'REQUESTS_STATUS':
      case 'REQUESTS_CANCEL_REQUEST':
      case 'REQUESTS_REPORT_FAULT':
      case 'REQUESTS_CONFIRM_SUBMIT':
      case 'REQUESTS_DISMISS_SUBMIT':
      case 'REQUESTS_BACK': {
        if (onRequestsVoiceCommand) {
          // Strip REQUESTS_ prefix to get command type
          const commandType = action.type.replace('REQUESTS_', '') as any;
          const response = onRequestsVoiceCommand({
            type: commandType,
            payload: 'payload' in action ? action.payload : undefined
          });
          if (response) {
            speak(response);
          } else {
            // Handler returned nothing (silent no-op) — reset state so overlay doesn't get stuck
            setVoiceState('idle');
          }
        } else {
          speak("My Room is not available right now.");
        }
        break;
      }

      default:
        speak(`I'm sorry, I don't understand that command.`);
    }
  }, [
    speak, guestName, guestContext, submitGuestRequest,
    currentView, isCancelConfirmShowing, isModifyConfirmShowing,
    onOpenCalendar, onOpenCalendarDay, onOpenBill, onOpenMessages, onOpenWeather, onHome, onOpenRequests,
    onCalendarNextDay, onCalendarPrevDay, onCalendarGoToDay, onCalendarCancelBooking, onCalendarBack, onCalendarSelectBooking, onCalendarNextBooking, onCalendarPrevBooking,
    onCalendarConfirmCancel, onCalendarKeepBooking,
    onCalendarConfirmModify, onCalendarCancelModify,
    onCalendarSetModifyTime, onCalendarSetModifyDay, onCalendarSetModifyParty,
    onRequestsVoiceCommand, onGreenStayVoiceCommand,
    onSwitchToSidebar, onSwitchToConcierge, onSwitchToTv,
    onTvChannel, onTvNextChannel, onTvPrevChannel, onTvFullscreen, onTvExitFullscreen,
    onTvMute, onTvUnmute, onTvOn, onTvOff,
    onOpenRoomService, onOpenSpa, onOpenExplore, onOpenRestaurant, onOpenGreenStay,
    onOpenBar, onOpenPool, onOpenGym, onOpenTransport,
    onBookBar, onBookPool, onBookGym, onBookTransport,
    bookingModalOpen, onBookingModalSetDate, onBookingModalSetTime, onBookingModalConfirm, onBookingModalCancel,
    onGreenStayEnable, onGreenStayDisable,
    skipCleaningDays, towelsOnlyDays, stayCheckIn, stayCheckOut,
    onSkipCleaningChange, onTowelsOnlyChange,
    isDemoMode, onDemoReset, onDemoRestartSession, onDemoClearBookings,
  ]);

  // Keep handleVoiceActionRef synced to the latest action handler
  useEffect(() => {
    handleVoiceActionRef.current = handleVoiceAction;
  }, [handleVoiceAction]);


  // ==========================================================================
  // SPACEBAR HANDLING (push-to-talk within overlay)
  // ==========================================================================

  useEffect(() => {
    if (!isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space' || e.repeat) return;
      const tag = document.activeElement?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;

      e.preventDefault();
      e.stopPropagation();

      if (voiceState === 'idle' || voiceState === 'speaking') {
        zaxiaVoice.cancel();
        startListening();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      e.preventDefault();
      e.stopPropagation();

      if (voiceState === 'listening') {
        stopListeningAndProcess();
      }
    };

    const handleEscape = (e: KeyboardEvent) => {
      if (e.code === 'Escape') {
        e.preventDefault();
        zaxiaVoice.cancel();
        if (recognitionRef.current) {
          recognitionRef.current.abort();
        }
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown, true);
    window.addEventListener('keyup', handleKeyUp, true);
    window.addEventListener('keydown', handleEscape, true);

    return () => {
      window.removeEventListener('keydown', handleKeyDown, true);
      window.removeEventListener('keyup', handleKeyUp, true);
      window.removeEventListener('keydown', handleEscape, true);
    };
  }, [isActive, voiceState, startListening, stopListeningAndProcess, onClose]);

  // ==========================================================================
  // AUTO-START LISTENING WHEN OVERLAY OPENS
  // ==========================================================================

  useEffect(() => {
    if (isActive) {
      // Small delay to let overlay animate in
      const timer = setTimeout(() => {
        startListeningRef.current();
      }, 100);
      return () => clearTimeout(timer);
    } else {
      // Cleanup when overlay deactivates — stop any running recognition/timers
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      zaxiaVoice.cancel();
      setVoiceState('idle');
      setTranscript('');
      setStatusMessage('');
    }
  }, [isActive]);

  // ==========================================================================
  // CLEANUP ON UNMOUNT
  // ==========================================================================

  useEffect(() => {
    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
      }
      zaxiaVoice.cancel();
    };
  }, []);

  // ==========================================================================
  // FIRE TV NATIVE VOICE BRIDGE
  // Handle `zaxia-native-voice` CustomEvents injected by the Android
  // SpeechRecognizer via webView.evaluateJavascript().  The desktop Web Speech
  // API path is unchanged — this is purely additive.
  // ==========================================================================

  useEffect(() => {
    const handleNativeVoice = (event: Event) => {
      const text = (event as CustomEvent<string>).detail?.trim();
      if (!text) return;

      // Mirror the same context-building used by recognition.onresult
      const viewMap: Record<string, 'grid' | 'room_service' | 'spa' | 'explore' | 'calendar' | 'requests' | 'tv' | 'overlay'> = {
        'grid': 'grid',
        'calendar': 'calendar',
        'requests': 'requests',
        'green-stay': 'overlay',
        'room_service': 'room_service',
        'spa': 'spa',
        'explore': 'explore',
        'tv': 'tv',
      };
      const effectiveView = viewMap[currentViewRef.current || 'grid'] || 'overlay';
      const voiceContext = createVoiceContext(effectiveView, {
        ecoModeEnabled: greenStayEnabledRef.current,
        subContext: {
          bookingModalOpen: bookingModalOpenRef.current,
        },
      });

      setTranscript(text);
      transcriptRef.current = text;

      console.log('[GlobalVoice] Native voice input:', text, 'view:', effectiveView);
      const action = processVoiceInput(text, voiceContext);
      console.log('[GlobalVoice] Native voice action:', action.type);

      if (action.type !== 'UNKNOWN') {
        handleVoiceActionRef.current(action, text);
      }
    };

    window.addEventListener('zaxia-native-voice', handleNativeVoice);
    return () => window.removeEventListener('zaxia-native-voice', handleNativeVoice);
  }, []);

  // ==========================================================================
  // RENDER
  // ==========================================================================

  return (
    <FloatingVoiceBar
      voiceState={voiceState}
      transcript={transcript}
      statusMessage={statusMessage}
    />
  );
}

export default GlobalVoiceOverlay;
